import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import FormLibraryRepository from '@/repositories/FormLibraryRepository';
import { Input, Modal } from '@/ui';
import rejectionMessage from '@/utils/rejectionMessage';

import fieldToProperty from '../schema/fieldToProperty';
import slugify from '../schema/slugify';
import type { FieldDef } from '../types';

const FAILED = 'Failed to add to library.';

interface AddToLibraryDialogProps {
  field: FieldDef;
  onClose: () => void;
  // Called with the new library question id so the field can link to it.
  onSaved: (questionId: string) => void;
}

const AddToLibraryDialog = ({ field, onClose, onSaved }: AddToLibraryDialogProps) => {
  const repo = FormLibraryRepository();
  const queryClient = useQueryClient();
  const [name, setName] = useState(field.label || field.key);
  const [key, setKey] = useState(field.key);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    // A brand-new library question: send the field's definition without any
    // questionId link so the backend mints a fresh shared question.
    const definition = { ...fieldToProperty({ ...field, label: name }) };
    delete definition['x-ui'].questionId;
    delete definition['x-ui'].inLibrary;

    try {
      const response = await repo.createQuestion({
        // Prefer a key derived from the user's key field, fall back to the name,
        // and only then to a generic slug — never an empty string.
        key: slugify(key, '') || slugify(name, 'question'),
        text: name,
        definition
      });
      if (response.isError || !response.data) {
        setError(response.errorMessage ?? FAILED);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['libraryQuestions'] });
      onSaved(response.data.id);
      onClose();
    } catch (failure) {
      // The repository rejects rather than returning on a 4xx — a duplicate key
      // is the everyday case — so without this the dialog would hang on its
      // spinner with the reason only in the console.
      setError(rejectionMessage(failure, FAILED));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add to Question Library"
      description="Reusable questions can be inserted into any form, staying linked to this one."
      size="md"
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
      primaryAction={{ label: 'Add to library', onClick: save, isLoading: saving }}>
      <div className="flex flex-col gap-3.5">
        <Input label="Library name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Key" value={key} onChange={(e) => setKey(e.target.value)} />
        <p className="rounded-lg bg-blue-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
          This field stays linked to the shared question, so later edits here reach every form using
          it. Unstar the field to give this form its own copy instead.
        </p>
        {error && <p className="text-[12.5px] text-red-500 dark:text-red-400">{error}</p>}
      </div>
    </Modal>
  );
};

export default AddToLibraryDialog;
